// DEALS: preflight screen for each teaser (Peter's five rules), the odds of raising on the timeline, and who to call.
import { DEAL_TYPES, REGIONS, SECTORS, type Deal, type DealResponse } from "../../shared/network";
import { fmtMoney, matchDeal, ODDS_CURVE, raiseOdds, screenDeal, type Bucket, type DealMatches, type Match, type RuleStatus } from "../../shared/engine";
import { TEASER_TASK, blankDeal, dealFromTeaser, type TeaserResult } from "../../shared/ai-tasks";
import type { MountFn } from "../context";
import { h, mount, svg } from "../ui/dom";
import { strip, type StripLine } from "../ui/strip";
import { emptyState, field, key, panel, seg, sw } from "../ui/widgets";

const PAGE = 40;
const TONE_CLASS: Record<RuleStatus, string> = { pass: "pass", warn: "warn", fail: "fail" };
const BAR: Record<RuleStatus, string> = { pass: "green", warn: "amber", fail: "red" };

function oddsChart(deal: Deal): SVGElement {
  const W = 360, H = 176, L = 34, R = 12, T = 12, B = 26;
  const X = (m: number) => L + (Math.min(24, Math.max(0, m)) / 24) * (W - L - R);
  const Y = (p: number) => T + (1 - p / 100) * (H - T - B);
  const g: SVGElement[] = [];
  for (const m of [0, 6, 12, 18, 24]) {
    g.push(svg("line", { x1: X(m), x2: X(m), y1: T, y2: H - B, stroke: "var(--line)", "stroke-width": 1 }));
    g.push(svg("text", { x: X(m), y: H - B + 13, "text-anchor": "middle" }, String(m)));
  }
  for (const p of [0, 25, 50, 75, 100]) {
    g.push(svg("line", { x1: L, x2: W - R, y1: Y(p), y2: Y(p), stroke: "var(--line)", "stroke-width": 1 }));
    g.push(svg("text", { x: L - 5, y: Y(p) + 3, "text-anchor": "end" }, p + "%"));
  }
  g.push(svg("text", { x: W - R, y: H - 2, "text-anchor": "end" }, "MONTHS TO RAISE"));
  const pts = ODDS_CURVE.filter(([m]) => m <= 24).map(([m, p]) => `${X(m).toFixed(1)},${Y(p).toFixed(1)}`).join(" ");
  g.push(svg("polyline", { points: pts, fill: "none", stroke: "var(--dim)", "stroke-width": 1.5 }));
  const mo = deal.timeline_months || 0;
  const odds = raiseOdds(mo);
  const x = X(mo);
  g.push(svg("line", { x1: x, x2: x, y1: T, y2: H - B, stroke: "var(--magenta)", "stroke-width": 1.5 }));
  g.push(svg("rect", { x: x - 2.5, y: Y(odds) - 2.5, width: 5, height: 5, fill: "var(--magenta)" }));
  const right = x > W * 0.55;
  g.push(svg("text", { x: right ? x - 6 : x + 6, y: right ? H - B - 6 : T + 9, "text-anchor": right ? "end" : "start", style: "fill:var(--magenta);font-weight:700;font-size:10px" }, `${mo} MO · ${odds}%`));
  return svg("svg", { viewBox: `0 0 ${W} ${H}`, role: "img", "aria-label": `Odds of raising in ${mo} months: ${odds} percent` }, g);
}

function dealFacts(d: Deal): HTMLElement {
  const f = (k: string, v: string) => h("div", null, h("span", { class: "micro" }, k), h("span", { class: "v" }, v));
  return h(
    "div",
    { class: "dealfacts" },
    f("RAISE", fmtMoney(d.raise)),
    f("MIN TICKET", fmtMoney(d.min_ticket)),
    f("STRUCTURE", d.structure || "—"),
    f("TYPE", d.deal_type || "—"),
    f("REGION", d.region || "—"),
    f("SPONSOR", d.sponsor || "—"),
    f("PRIOR RAISES", String(d.sponsor_prior_raises)),
    f("SECTORS", d.sectors.join(", ") || "—"),
  );
}

export const mountDeals: MountFn = (el, ctx, params) => {
  if (!ctx.network) {
    mount(el, h("div", { class: "pagehead" }, h("h1", { class: "title" }, "Deals")), emptyState(() => ctx.navigate("sources")));
    return;
  }
  let selId: string | null = params?.id ?? null;
  let editing: { deal: Deal; isNew: boolean } | null = null;
  let bucket: Bucket = "bring";
  let shown = PAGE;
  let selK: string | null = null;
  let confirmDel = false;
  let cache: { id: string; m: DealMatches } | null = null;

  const left = h("div");
  const main = h("div");
  mount(el, h("div", { class: "pagehead" }, h("h1", { class: "title" }, "Deals"), h("span", { class: "grow" })), h("div", { class: "deals" }, left, main));

  const current = (): Deal | null => ctx.state.deals.find((d) => d.deal_id === selId) ?? ctx.state.deals[0] ?? null;

  function matches(d: Deal): DealMatches {
    if (!cache || cache.id !== d.deal_id) cache = { id: d.deal_id, m: matchDeal(ctx.network!, d, { asOf: ctx.asOf, notes: ctx.state.notes, responses: ctx.state.responses, contacted: ctx.state.contacted }) };
    return cache.m;
  }

  function renderLeft() {
    const cur = current();
    mount(
      left,
      h(
        "div",
        { class: "dlist" },
        h("div", { class: "hd" }, h("span", { class: "micro" }, `DEALS  ${ctx.state.deals.length}`), key("NEW DEAL", () => ((editing = { deal: blankDeal(ctx.asOf), isNew: true }), render()), { sm: true, primary: true })),
        ctx.state.deals.map((d) => {
          const s = screenDeal(d);
          const row = h(
            "div",
            { class: "drow" + (!editing && cur?.deal_id === d.deal_id ? " sel" : ""), tabIndex: 0, role: "button" },
            h("div", { class: "b " + BAR[s.tone] }),
            h("div", { class: "m" }, h("div", { class: "n" }, d.codename), h("div", { class: "d" }, "RCVD " + (d.received || "—"))),
            h("div", { class: "l" }, h("span", { class: `lamp lit ${s.tone === "pass" ? "green" : s.tone === "warn" ? "amber" : "red"}` }, s.verdict === "Proceed with caution" ? "CAUTION" : s.verdict === "Take it" ? "TAKE" : s.verdict.toUpperCase())),
          );
          const open = () => {
            editing = null;
            selK = null;
            bucketReset();
            ctx.navigate("deals", { id: d.deal_id });
          };
          row.addEventListener("click", open);
          row.addEventListener("keydown", (e) => (e.key === "Enter" ? open() : null));
          return row;
        }),
        ctx.state.deals.length ? null : h("div", { class: "hd muted mono", style: "border:0;font-size:12px" }, "No deals yet."),
      ),
    );
  }
  function bucketReset() {
    bucket = "bring";
    shown = PAGE;
    confirmDel = false;
  }

  /* ---------- preflight ---------- */
  function matchStrip(m: Match, d: Deal): HTMLElement {
    const lines: StripLine[] = [];
    for (const t of m.reasons) lines.push({ tone: "green", text: t });
    for (const t of m.cautions) lines.push({ tone: "amber", text: t });
    for (const t of m.whyNow) lines.push({ tone: "amber", text: "Why now: " + t });
    if (m.hold) lines.push({ tone: "red", text: "On hold: " + m.hold.why });
    const resp = ctx.state.responses[d.deal_id]?.[m.c.k] as DealResponse | undefined;
    const rk = (label: string, v: DealResponse) => key(label, () => void ctx.setResponse(d.deal_id, m.c.k, resp === v ? null : v), { on: resp === v, sm: true });
    return strip({
      c: m.c,
      daysSince: m.s.daysSince,
      bar: m.hold ? "red" : resp ? "cyan" : m.whyNow.length ? "amber" : m.bucket === "bring" ? "green" : "none",
      lines,
      selected: selK === m.c.k,
      onSelect: () => ((selK = selK === m.c.k ? null : m.c.k), renderMain()),
      keys: [h("div", { class: "keys col" }, [rk("INTERESTED", "interested"), rk("LATER", "later"), rk("PASSED", "passed")])],
    });
  }

  function preflight(d: Deal): HTMLElement {
    const s = screenDeal(d);
    const m = matches(d);
    const buckets: { value: Bucket; label: string }[] = [
      { value: "bring", label: `BRING IT UP (${m.counts.bring})` },
      { value: "reconnect", label: `TALK ABOUT ANYTHING ELSE (${m.counts.reconnect})` },
      { value: "hold", label: `ON HOLD (${m.counts.hold})` },
      { value: "responded", label: `RESPONDED (${m.counts.responded})` },
    ];
    const rows = m[bucket];
    const delBtn = confirmDel
      ? h("span", { class: "confirm" }, `DELETE ${d.codename}?`, key("YES, DELETE", () => ((confirmDel = false), void ctx.removeDeal(d.deal_id)), { danger: true, sm: true }), key("NO", () => ((confirmDel = false), renderMain()), { sm: true }))
      : key("DELETE", () => ((confirmDel = true), renderMain()), { danger: true, sm: true });
    const checklist = h(
      "ul",
      { class: "rules" },
      s.rules.map((r) =>
        h(
          "li",
          null,
          h("div", { class: "r" }, h("span", { class: "n" }, String(r.n)), h("span", null, r.rule), h("span", { class: "dots" }), h("span", { class: "res " + TONE_CLASS[r.status] }, r.status.toUpperCase())),
          h("div", { class: "why" }, r.why),
        ),
      ),
    );
    const top = panel(
      "PREFLIGHT  " + d.codename.toUpperCase(),
      [
        h("p", { class: "muted", style: "margin:0 0 12px;max-width:80ch" }, d.one_liner || d.summary || "No description."),
        h(
          "div",
          { class: "pf" },
          h(
            "div",
            null,
            h("div", { class: "row", style: "margin-bottom:10px" }, h("span", { class: "verdict " + s.tone }, s.verdict), h("span", { class: "micro" }, "VERDICT")),
            h("p", { style: "margin:0 0 6px;max-width:60ch" }, s.advice),
            s.disqualifiers.length ? h("div", { class: "dq" }, s.disqualifiers.map((t) => h("div", null, t))) : null,
            checklist,
          ),
          h("div", { class: "odds" }, h("div", { class: "micro", style: "margin-bottom:4px" }, "ODDS OF RAISING BY TIMELINE"), oddsChart(d), dealFacts(d)),
        ),
      ],
      h("div", { class: "keys" }, key("EDIT", () => ((editing = { deal: structuredClone(d), isNew: false }), render()), { sm: true }), delBtn),
      "mg",
    );
    const list = h(
      "div",
      null,
      h("div", { style: "margin:0 0 10px" }, seg(buckets, bucket, (v) => ((bucket = v), (shown = PAGE), renderMain()), "mgseg")),
      h("div", { class: "strips" }, rows.length ? rows.slice(0, shown).map((x) => matchStrip(x, d)) : h("div", { class: "empty", style: "border:0" }, h("div", { class: "t muted" }, "NONE IN THIS GROUP"))),
      rows.length > shown ? h("div", { class: "stripmore" }, key(`SHOW ${Math.min(PAGE, rows.length - shown)} MORE`, () => ((shown += PAGE), renderMain()))) : null,
    );
    return h("div", null, top, list);
  }

  /* ---------- form ---------- */
  function dealForm(): HTMLElement {
    const ed = editing!;
    const d = ed.deal;
    const inp = (k: keyof Deal, type = "text", extra: Record<string, unknown> = {}) => {
      const i = h("input", { type, value: String(d[k] ?? ""), ...extra });
      i.addEventListener("input", () => {
        (d as unknown as Record<string, unknown>)[k] = type === "number" ? (i.value === "" ? 0 : Number(i.value)) : i.value;
        if (money[k as string]) money[k as string].textContent = fmtMoney(Number(i.value) || 0);
      });
      return i;
    };
    const money: Record<string, HTMLElement> = {};
    const moneyField = (label: string, k: "raise" | "min_ticket") => {
      money[k] = h("span", { class: "num cy", style: "margin-left:8px" }, fmtMoney(d[k]));
      const f = field(label, inp(k, "number", { min: 0, step: 50000 }));
      f.firstElementChild!.appendChild(money[k]);
      return f;
    };
    const selectOf = (k: "structure" | "region", opts: string[]) => {
      const s = h("select", null, h("option", { value: "" }, "—"), opts.map((o) => h("option", { value: o, selected: d[k] === o }, o)));
      s.addEventListener("change", () => (d[k] = s.value));
      return s;
    };
    const toggles = (k: "sectors" | "adjacent") => {
      const box = h("div", { class: "togs" });
      for (const s of SECTORS) {
        const b = key(s.toUpperCase(), () => {
          const arr = d[k];
          const i = arr.indexOf(s);
          if (i >= 0) arr.splice(i, 1);
          else arr.push(s);
          b.classList.toggle("on", i < 0);
          b.setAttribute("aria-pressed", String(i < 0));
        }, { on: d[k].includes(s), sm: true });
        box.appendChild(b);
      }
      return box;
    };
    const gutSeg = h("div");
    const paintGut = () => mount(gutSeg, seg([1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) })), String(d.culture_gut), (v) => ((d.culture_gut = Number(v)), paintGut())));
    paintGut();
    const flags = h("input", { type: "text", value: d.values_flags.join(", "), placeholder: "cannabis, gambling" });
    flags.addEventListener("input", () => (d.values_flags = flags.value.split(",").map((x) => x.trim()).filter(Boolean)));
    const summary = h("textarea", { rows: 3 });
    summary.value = d.summary;
    summary.addEventListener("input", () => (d.summary = summary.value));

    let teaser: HTMLElement | null = null;
    if (ctx.ai) {
      const ta = h("textarea", { rows: 6, placeholder: "Paste the teaser", "aria-label": "Paste the teaser" });
      const status = h("span", { class: "mono muted", style: "font-size:12px" });
      const read = key("READ TEASER", async () => {
        if (!ta.value.trim()) return;
        read.disabled = true;
        status.textContent = "READING…";
        try {
          const r = await ctx.ai!<Partial<TeaserResult>>(TEASER_TASK.task, { system: TEASER_TASK.system, prompt: ta.value, schema: TEASER_TASK.schema, effort: TEASER_TASK.effort });
          const next = dealFromTeaser(r, ctx.asOf);
          if (!ed.isNew) next.deal_id = d.deal_id;
          next.culture_gut = d.culture_gut;
          ed.deal = next;
          renderMain();
          ctx.toast("Fields filled. Check them before saving.");
        } catch (e) {
          status.textContent = "";
          read.disabled = false;
          ctx.toast((e as Error).message || "Couldn't read that.", "err");
        }
      }, { title: "Uses Claude to fill the fields; you review before saving", sm: true });
      teaser = h("div", { class: "wide" }, h("span", { class: "micro", style: "display:block;margin-bottom:4px" }, "PASTE THE TEASER"), ta, h("div", { class: "row", style: "margin-top:8px" }, read, status));
    }

    const body = h(
      "div",
      null,
      teaser ? h("div", { class: "formgrid", style: "margin-bottom:14px" }, teaser) : null,
      h(
        "div",
        { class: "formgrid" },
        field("CODENAME", inp("codename")),
        field("RECEIVED", inp("received", "date")),
        field("SPONSOR", inp("sponsor")),
        field("DEAL TYPE", inp("deal_type", "text", { placeholder: "Carve-out buyout" })),
        field("STRUCTURE", selectOf("structure", DEAL_TYPES)),
        field("REGION", selectOf("region", REGIONS)),
        moneyField("RAISE (USD)", "raise"),
        moneyField("MIN TICKET (USD)", "min_ticket"),
        field("MONTHS TO RAISE", inp("timeline_months", "number", { min: 0, step: 0.5 })),
        field("SPONSOR PRIOR RAISES", inp("sponsor_prior_raises", "number", { min: 0 })),
        field("CULTURE GUT (1-5)", gutSeg),
        field("VALUES FLAGS (comma separated)", flags),
        field("ONE-LINER", inp("one_liner"), "wide"),
        field("SUMMARY", summary, "wide"),
        h("div", { class: "wide" }, h("span", { class: "micro", style: "display:block;margin-bottom:6px" }, "SECTORS"), toggles("sectors")),
        h("div", { class: "wide" }, h("span", { class: "micro", style: "display:block;margin-bottom:6px" }, "ADJACENT SECTORS"), toggles("adjacent")),
        h(
          "div",
          { class: "wide row", style: "gap:22px" },
          sw("RETAINER OFFERED", d.retainer_offered, (v) => (d.retainer_offered = v)),
          sw("FUND", d.is_fund, (v) => (d.is_fund = v)),
          sw("FIRST-TIME FUND", d.first_time_fund, (v) => (d.first_time_fund = v)),
          sw("FRIEND IS THE GP", !!d.friend_gp, (v) => (d.friend_gp = v)),
        ),
      ),
      h(
        "div",
        { class: "keys", style: "margin-top:16px" },
        key("SAVE", async () => {
          if (!d.codename.trim()) return ctx.toast("Give the deal a codename.", "warn");
          d.codename = d.codename.trim();
          editing = null;
          selId = d.deal_id;
          cache = null;
          bucketReset();
          await ctx.saveDeal(d);
          ctx.navigate("deals", { id: d.deal_id });
          render();
        }, { primary: true }),
        key("CANCEL", () => ((editing = null), render())),
      ),
    );
    return panel(ed.isNew ? "NEW DEAL" : "EDIT  " + d.codename.toUpperCase(), body, null, "mgp");
  }

  function renderMain() {
    if (editing) return mount(main, dealForm());
    const d = current();
    if (!d) return mount(main, h("div", { class: "empty" }, h("div", { class: "t" }, "NO DEALS. Add one when a teaser comes in."), key("NEW DEAL", () => ((editing = { deal: blankDeal(ctx.asOf), isNew: true }), render()), { primary: true })));
    mount(main, preflight(d));
  }
  function render() {
    renderLeft();
    renderMain();
  }

  render();
  const off = ctx.onChange(() => {
    cache = null;
    if (editing) return renderLeft(); // don't rebuild a form Peter is typing in
    render();
  });
  const onParams = (e: Event) => {
    const p = (e as CustomEvent<Record<string, string>>).detail;
    selId = p.id ?? null;
    cache = null;
    render();
  };
  el.addEventListener("params", onParams);
  return () => {
    off();
    el.removeEventListener("params", onParams);
  };
};

